import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Pagination } from 'react-bootstrap';
import adminService from '../../../../services/AdminService';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    user_counts: {
      distributors: { total: 0, active: 0, inactive: 0 },
      fos: { total: 0, active: 0, inactive: 0 },
      retailers: { total: 0, active: 0, inactive: 0 }
    },
    summary: {
      total_users: 0,
      active_users: 0,
      inactive_users: 0,
      today_registrations: 0,
      this_week_registrations: 0,
      this_month_registrations: 0
    },
    last_updated: null
  });
  const [registrationTrends, setRegistrationTrends] = useState({
    period: 'weekly',
    trends: [],
    summary: {
      total_registrations: 0,
      average_daily: 0,
      peak_day: '',
      peak_count: 0
    }
  });
  const [recentActivities, setRecentActivities] = useState({
    activities: [],
    pagination: {
      total: 0,
      current_page: 1,
      total_pages: 1,
      has_more: false
    }
  });
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [loading, setLoading] = useState({
    dashboard: false,
    trends: false,
    activities: false
  });
  const [error, setError] = useState({
    dashboard: '',
    trends: '',
    activities: ''
  });
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchAllDashboardData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchAllDashboardData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchAllDashboardData = () => {
    fetchDashboardOverview();
    fetchRegistrationTrends();
    fetchRecentActivities(1); // Reset to first page on refresh
    setActivitiesPage(1);
  };

  const fetchDashboardOverview = async () => {
    setLoading(prev => ({ ...prev, dashboard: true }));
    setError(prev => ({ ...prev, dashboard: '' }));
    
    try {
      const result = await adminService.getDashboardOverview();
      if (result.success) {
        setDashboardData(result.data.data);
        setLastRefresh(new Date());
      } else {
        setError(prev => ({ ...prev, dashboard: result.error }));
      }
    } catch (err) {
      setError(prev => ({ ...prev, dashboard: 'Failed to fetch dashboard data' }));
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  };

  const fetchRegistrationTrends = async (period = 'weekly') => {
    setLoading(prev => ({ ...prev, trends: true }));
    setError(prev => ({ ...prev, trends: '' }));
    
    try {
      const result = await adminService.getRegistrationTrends(period);
      if (result.success) {
        setRegistrationTrends(result.data.data);
      } else {
        setError(prev => ({ ...prev, trends: result.error }));
      }
    } catch (err) {
      setError(prev => ({ ...prev, trends: 'Failed to fetch trends data' }));
    } finally {
      setLoading(prev => ({ ...prev, trends: false }));
    }
  };

  const fetchRecentActivities = async (page = 1) => {
    setLoading(prev => ({ ...prev, activities: true }));
    setError(prev => ({ ...prev, activities: '' }));
    
    try {
      const limit = 3; // Show only 3 records
      const offset = (page - 1) * limit;
      const result = await adminService.getRecentActivities(limit, offset);
      
      if (result.success) {
        setRecentActivities(result.data.data);
        setActivitiesPage(page);
      } else {
        setError(prev => ({ ...prev, activities: result.error }));
      }
    } catch (err) {
      setError(prev => ({ ...prev, activities: 'Failed to fetch activities data' }));
    } finally {
      setLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const handleActivitiesPageChange = (page) => {
    fetchRecentActivities(page);
  };

  const handleRefresh = () => {
    fetchAllDashboardData();
    toast.success('Dashboard refreshed successfully!');
  };

  const StatCard = ({ icon, title, value, subtitle, iconColor = "#031a6f", loading = false }) => (
    <Card className="border-0 shadow-sm h-100" style={{
      borderRadius: '16px'
    }}>
      <Card.Body style={{ background: '#ffffff' }}>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <div className="mt-2 text-muted">Loading...</div>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-start">
            <div className="text-dark">
              <div className="fs-6 mb-2 text-muted">{title}</div>
              <div className="fs-2 fw-bold mb-1" style={{ color: '#031a6f' }}>{value?.toLocaleString() || 0}</div>
              {subtitle && <div className="fs-7 text-muted">{subtitle}</div>}
            </div>
            <div className="p-3 rounded-circle" style={{ backgroundColor: `${iconColor}15` }}>
              <i className={`${icon} fs-4`} style={{ color: iconColor }}></i>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  const getIconColorClass = (iconColor) => {
    const colorMap = {
      'success': 'text-success',
      'primary': 'text-primary',
      'warning': 'text-warning',
      'danger': 'text-danger',
      'info': 'text-info'
    };
    return colorMap[iconColor] || 'text-secondary';
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0" style={{ color: '#031a6f', fontWeight: '700' }}>
          <i className="fas fa-tachometer-alt me-2" style={{ color: '#031a6f' }}></i>
          Dashboard
        </h2>
        <div className="d-flex align-items-center gap-3">
          <small className="text-muted">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </small>
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={handleRefresh}
            disabled={loading.dashboard || loading.trends || loading.activities}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </Button>
        </div>
      </div>

      {error.dashboard && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error.dashboard}
        </Alert>
      )}

      {/* Main User Counts Row */}
      <Row className="g-4 mb-4">
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-building"
            title="Distributors"
            value={dashboardData.user_counts.distributors.total}
            subtitle={`${dashboardData.user_counts.distributors.active} Active, ${dashboardData.user_counts.distributors.inactive} Inactive`}
            iconColor="#031a6f"
            loading={loading.dashboard}
          />
        </Col>
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-users"
            title="Field Officers (FOS)"
            value={dashboardData.user_counts.fos.total}
            subtitle={`${dashboardData.user_counts.fos.active} Active, ${dashboardData.user_counts.fos.inactive} Inactive`}
            iconColor="#059669"
            loading={loading.dashboard}
          />
        </Col>
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-store"
            title="Retailers"
            value={dashboardData.user_counts.retailers.total}
            subtitle={`${dashboardData.user_counts.retailers.active} Active, ${dashboardData.user_counts.retailers.inactive} Inactive`}
            iconColor="#dc2626"
            loading={loading.dashboard}
          />
        </Col>
      </Row>

      {/* Additional Stats Row */}
      <Row className="g-4 mb-4">
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-user-check"
            title="Total Users"
            value={dashboardData.summary.total_users}
            subtitle="All System Users"
            iconColor="#7c3aed"
            loading={loading.dashboard}
          />
        </Col>
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-circle-dot"
            title="Active Users"
            value={dashboardData.summary.active_users}
            subtitle={`${dashboardData.summary.inactive_users} Inactive`}
            iconColor="#0891b2"
            loading={loading.dashboard}
          />
        </Col>
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-calendar-plus"
            title="Today's Registrations"
            value={dashboardData.summary.today_registrations}
            subtitle={`Week: ${dashboardData.summary.this_week_registrations}, Month: ${dashboardData.summary.this_month_registrations}`}
            iconColor="#ea580c"
            loading={loading.dashboard}
          />
        </Col>
      </Row>

      {/* Charts and Activities Row */}
      <Row className="g-4">
        <Col lg={8}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0" style={{ color: '#031a6f', fontWeight: '600' }}>
                  <i className="fas fa-chart-line me-2" style={{ color: '#031a6f' }}></i>
                  Registration Trends - {registrationTrends.period}
                </h5>
                <div className="d-flex gap-2">
                  <Button
                    variant={registrationTrends.period === 'daily' ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => fetchRegistrationTrends('daily')}
                    disabled={loading.trends}
                  >
                    Daily
                  </Button>
                  <Button
                    variant={registrationTrends.period === 'weekly' ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => fetchRegistrationTrends('weekly')}
                    disabled={loading.trends}
                  >
                    Weekly
                  </Button>
                  <Button
                    variant={registrationTrends.period === 'monthly' ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => fetchRegistrationTrends('monthly')}
                    disabled={loading.trends}
                  >
                    Monthly
                  </Button>
                </div>
              </div>
              
              {loading.trends ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading trends...</div>
                </div>
              ) : error.trends ? (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error.trends}
                </Alert>
              ) : (
                <>
                  {/* Trends Summary */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-primary">{registrationTrends.summary.total_registrations}</div>
                        <small className="text-muted">Total Registrations</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-success">{registrationTrends.summary.average_daily}</div>
                        <small className="text-muted">Daily Average</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-warning">{registrationTrends.summary.peak_count}</div>
                        <small className="text-muted">Peak Day Count</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-6 fw-bold text-info">{registrationTrends.summary.peak_day ? new Date(registrationTrends.summary.peak_day).toLocaleDateString() : 'N/A'}</div>
                        <small className="text-muted">Peak Day</small>
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Trends Data */}
                  {registrationTrends.trends.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Distributors</th>
                            <th>FOS</th>
                            <th>Retailers</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrationTrends.trends.slice(-7).map((trend, index) => (
                            <tr key={index}>
                              <td>{new Date(trend.date).toLocaleDateString()}</td>
                              <td><span className="badge bg-primary">{trend.distributors}</span></td>
                              <td><span className="badge bg-success">{trend.fos}</span></td>
                              <td><span className="badge bg-warning">{trend.retailers}</span></td>
                              <td><strong>{trend.total}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-chart-bar fs-1 text-muted mb-3 d-block"></i>
                      <p className="text-muted">No trends data available</p>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0" style={{ color: '#031a6f', fontWeight: '600' }}>
                  <i className="fas fa-bell me-2" style={{ color: '#031a6f' }}></i>
                  Recent Activities
                </h5>
                <small className="text-muted">
                  Page {activitiesPage} of {recentActivities.pagination.total_pages} ({recentActivities.pagination.total} total)
                </small>
              </div>
              
              {loading.activities ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <div className="mt-2 text-muted">Loading activities...</div>
                </div>
              ) : error.activities ? (
                <Alert variant="danger" className="mb-0">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error.activities}
                </Alert>
              ) : recentActivities.activities.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                  {recentActivities.activities.map((activity) => (
                    <div key={activity.id} className="d-flex align-items-start gap-2">
                      <div className={`bg-${activity.icon_color} bg-opacity-10 p-2 rounded-circle`}>
                        <i className={`fas fa-${activity.icon} ${getIconColorClass(activity.icon_color)}`}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{activity.title}</div>
                        <div className="small text-muted mb-1">{activity.description}</div>
                        <small className="text-muted">
                          <i className="fas fa-clock me-1"></i>
                          {activity.time_ago}
                        </small>
                      </div>
                    </div>
                  ))}
                  
                  {recentActivities.pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination size="sm">
                        <Pagination.Prev 
                          onClick={() => handleActivitiesPageChange(activitiesPage - 1)}
                          disabled={activitiesPage === 1 || loading.activities}
                        />
                        
                        {[...Array(Math.min(3, recentActivities.pagination.total_pages))].map((_, index) => {
                          let pageNumber;
                          if (recentActivities.pagination.total_pages <= 3) {
                            pageNumber = index + 1;
                          } else if (activitiesPage <= 2) {
                            pageNumber = index + 1;
                          } else if (activitiesPage >= recentActivities.pagination.total_pages - 1) {
                            pageNumber = recentActivities.pagination.total_pages - 2 + index;
                          } else {
                            pageNumber = activitiesPage - 1 + index;
                          }
                          
                          return (
                            <Pagination.Item
                              key={pageNumber}
                              active={pageNumber === activitiesPage}
                              onClick={() => handleActivitiesPageChange(pageNumber)}
                              disabled={loading.activities}
                            >
                              {pageNumber}
                            </Pagination.Item>
                          );
                        })}
                        
                        <Pagination.Next 
                          onClick={() => handleActivitiesPageChange(activitiesPage + 1)}
                          disabled={activitiesPage === recentActivities.pagination.total_pages || loading.activities}
                        />
                      </Pagination>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-inbox fs-1 text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">No recent activities</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;